import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

interface KioskSectionProps {
  kioskVideo: string;
  kioskFallback: string;
  kioskContents: string;
  kioskVideoRef: React.RefObject<HTMLVideoElement | null>;
  kioskSectionRef: React.RefObject<HTMLDivElement | null>;
  isIOS?: boolean;
  kioskColorVideo?: string;
  kioskAlphaVideo?: string;
}

export const KioskSection: React.FC<KioskSectionProps> = ({
  kioskVideo,
  kioskFallback,
  kioskContents,
  kioskVideoRef,
  kioskSectionRef,
  isIOS = false,
  kioskColorVideo,
  kioskAlphaVideo,
}) => {
  const [isKioskVideoPlaying, setIsKioskVideoPlaying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colorVideoRef = useRef<HTMLVideoElement>(null);
  const alphaVideoRef = useRef<HTMLVideoElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const syncCheckIntervalRef = useRef<number | null>(null);

  // 비디오 파일의 타입 결정 (WebP는 이미지로 처리)
  const isWebP = kioskVideo.endsWith(".webp");
  const videoType = kioskVideo.endsWith(".mov") ? "video/mp4" : "video/webm";

  // iOS에서 Canvas 합성 사용 여부
  const useCanvasComposite = isIOS && kioskColorVideo && kioskAlphaVideo;

  // Kiosk Video Intersection Observer - 섹션 중간에 오면 재생 시작
  useEffect(() => {
    // WebP는 자동 재생되므로 제어 불필요
    if (isWebP) return;

    // Canvas 합성 모드는 별도 처리
    if (useCanvasComposite) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && kioskVideoRef.current) {
            // 섹션이 보이면 재생
            if (!isKioskVideoPlaying) {
              kioskVideoRef.current.currentTime = 0; // 처음부터 재생
              kioskVideoRef.current.play().catch((error) => {
                console.error("Kiosk video play failed:", error);
              });
              setIsKioskVideoPlaying(true);
            }
          } else {
            // 섹션이 화면에서 벗어나면 상태 리셋
            if (isKioskVideoPlaying && kioskVideoRef.current) {
              kioskVideoRef.current.pause();
              kioskVideoRef.current.currentTime = 0;
              setIsKioskVideoPlaying(false);
            }
          }
        });
      },
      {
        threshold: 0.3, // 섹션의 30%가 보이면 트리거
      }
    );

    if (kioskSectionRef.current) {
      observer.observe(kioskSectionRef.current);
    }

    return () => {
      if (kioskSectionRef.current) {
        observer.unobserve(kioskSectionRef.current);
      }
    };
  }, [
    isKioskVideoPlaying,
    kioskVideoRef,
    kioskSectionRef,
    isWebP,
    useCanvasComposite,
  ]);

  // iOS Canvas 합성 로직
  useEffect(() => {
    if (!useCanvasComposite) return;

    const canvas = canvasRef.current;
    const vColor = colorVideoRef.current;
    const vAlpha = alphaVideoRef.current;

    if (!canvas || !vColor || !vAlpha) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    // 프레임 동기화 함수
    const syncVideos = () => {
      if (!vColor || !vAlpha) return;

      const timeDiff = Math.abs(vColor.currentTime - vAlpha.currentTime);
      const syncThreshold = 0.05; // 50ms 차이까지 허용

      if (timeDiff > syncThreshold) {
        // Alpha 비디오를 Color 비디오에 맞춤
        vAlpha.currentTime = vColor.currentTime;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 섹션이 보이면 재생 시작
            Promise.all([
              vColor.play().catch(() => {}),
              vAlpha.play().catch(() => {}),
            ]).then(() => {
              canvas.width = vColor.videoWidth;
              canvas.height = vColor.videoHeight;

              const loop = () => {
                if (!vColor.paused && !vAlpha.paused) {
                  // 매 프레임마다 동기화 체크
                  syncVideos();

                  // Color 비디오를 캔버스에 그리기
                  ctx.drawImage(vColor, 0, 0, canvas.width, canvas.height);
                  const img = ctx.getImageData(
                    0,
                    0,
                    canvas.width,
                    canvas.height
                  );
                  const data = img.data;

                  // Alpha 비디오를 임시 캔버스로 가져오기
                  const tmp = document.createElement("canvas");
                  tmp.width = canvas.width;
                  tmp.height = canvas.height;
                  const tctx = tmp.getContext("2d", {
                    willReadFrequently: true,
                  });
                  if (tctx) {
                    tctx.drawImage(vAlpha, 0, 0, canvas.width, canvas.height);
                    const aimg = tctx.getImageData(
                      0,
                      0,
                      canvas.width,
                      canvas.height
                    ).data;

                    // 그레이스케일 Alpha 채널을 RGB 이미지의 Alpha로 복사
                    for (let i = 0, j = 0; i < data.length; i += 4, j += 4) {
                      data[i + 3] = aimg[j]; // R 채널을 Alpha로 사용
                    }
                    ctx.putImageData(img, 0, 0);
                  }
                }
                animationFrameRef.current = requestAnimationFrame(loop);
              };
              loop();

              // 정기적인 동기화 체크 시작
              if (syncCheckIntervalRef.current === null) {
                syncCheckIntervalRef.current = window.setInterval(() => {
                  if (!vColor.paused && !vAlpha.paused) {
                    syncVideos();
                  }
                }, 100); // 100ms마다 체크
              }
            });
          } else {
            // 섹션이 화면에서 벗어나면 재생 중지
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current);
              animationFrameRef.current = null;
            }
            if (syncCheckIntervalRef.current !== null) {
              clearInterval(syncCheckIntervalRef.current);
              syncCheckIntervalRef.current = null;
            }
            vColor.pause();
            vAlpha.pause();
            vColor.currentTime = 0;
            vAlpha.currentTime = 0;
          }
        });
      },
      {
        threshold: 0.3,
      }
    );

    if (kioskSectionRef.current) {
      observer.observe(kioskSectionRef.current);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (syncCheckIntervalRef.current !== null) {
        clearInterval(syncCheckIntervalRef.current);
      }
      if (kioskSectionRef.current) {
        observer.unobserve(kioskSectionRef.current);
      }
    };
  }, [useCanvasComposite, kioskSectionRef]);

  return (
    <section
      ref={kioskSectionRef}
      className="py-32 bg-gradient-to-b from-pink-50 via-purple-50 to-blue-50 relative overflow-hidden"
    >
      <div className="relative z-10">
        <motion.div
          className="text-center mb-16 px-6 md:px-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
            차트 + 키오스크
          </h2>
          <h3 className="text-3xl md:text-5xl font-bold mb-6 text-gray-800">
            드림팀
          </h3>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto">
            키오스크를 원한다면 필수인 차트 시스템. 빠른 접수, 편리한 수납.
            한의원에 완벽하게 맞춘 키오스크 메뉴 선택. 환자들에게 가장 친화적인
            구성.
          </p>
        </motion.div>

        <motion.div
          className="mb-16 flex justify-center mt-0 md:-mt-[200px]"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative inline-block">
            {useCanvasComposite ? (
              // iOS: Canvas 합성 방식 (color + alpha)
              <>
                <canvas
                  ref={canvasRef}
                  style={{
                    willChange: "transform",
                    transform: "translateZ(0)",
                    maxWidth: "70vw",
                  }}
                  className="h-auto"
                />
                {/* Hidden videos for canvas compositing */}
                <video
                  ref={colorVideoRef}
                  loop
                  muted
                  playsInline
                  preload="auto"
                  style={{ display: "none" }}
                >
                  <source src={kioskColorVideo} type="video/mp4" />
                </video>
                <video
                  ref={alphaVideoRef}
                  loop
                  muted
                  playsInline
                  preload="auto"
                  style={{ display: "none" }}
                >
                  <source src={kioskAlphaVideo} type="video/mp4" />
                </video>
              </>
            ) : isWebP ? (
              // Animated WebP는 img 태그로 무한 반복
              <img
                src={kioskVideo}
                alt="Kiosk"
                style={{
                  willChange: "transform",
                  transform: "translateZ(0)",
                  maxWidth: "70vw",
                }}
                className="h-auto"
              />
            ) : (
              // WebM/MOV는 video 태그로 제어
              <video
                ref={kioskVideoRef}
                loop
                muted
                playsInline
                preload="auto"
                poster={kioskFallback}
                onError={(e) => {
                  const video = e.currentTarget;
                  video.style.display = "none";
                  const fallbackImg = document.createElement("img");
                  fallbackImg.src = kioskFallback;
                  fallbackImg.alt = "Kiosk";
                  fallbackImg.style.cssText = video.style.cssText;
                  fallbackImg.className = video.className;
                  video.parentElement?.appendChild(fallbackImg);
                }}
                style={{
                  willChange: "transform",
                  transform: "translateZ(0)",
                  maxWidth: "70vw",
                }}
                className="h-auto"
              >
                <source src={kioskVideo} type={videoType} />
                <img
                  src={kioskFallback}
                  alt="Kiosk Fallback"
                  style={{
                    willChange: "transform",
                    transform: "translateZ(0)",
                    maxWidth: "70vw",
                  }}
                  className="h-auto"
                />
              </video>
            )}

            <img
              src={kioskContents}
              alt="Kiosk Features"
              className="absolute pointer-events-none w-auto h-[18%] top-[35%] left-[9%]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
